import React from "react";
import { useField } from "formik";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const InputField = ({
  disable,
  required,
  label,
  isPassword,
  showPassword,
  onPasswordToggle,
  isCheckbox,
  isToggle,
  leftIcon: LeftIcon,
  ...props
}) => {
  const [field, meta, helpers] = useField(props);

  return (
    <div className="form-control w-full">
      {/* Label */}
      {!isCheckbox && !isToggle && label && (
        <label
          htmlFor={props.id || props.name}
          className="block text-sm font-semibold text-gray-700 mb-1.5"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Checkbox / Toggle */}
      {(isCheckbox || isToggle) && (
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              {...field}
              {...props}
              checked={field.value}
              onChange={(e) => helpers.setValue(e.target.checked)}
            />
            <div
              className={`
                relative w-11 h-6
                bg-gray-200 peer-focus:outline-none
                rounded-full peer
                peer-checked:after:translate-x-full
                peer-checked:after:border-white
                after:content-['']
                after:absolute after:top-[2px] after:left-[2px]
                after:bg-white after:border-gray-300 after:border
                after:rounded-full after:h-5 after:w-5
                after:transition-all
                ${field.value ? "bg-primary" : "bg-gray-200"}
              `}
            />
            <span className="ml-3 text-sm font-medium text-gray-700">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </span>
          </label>
        </div>
      )}

      {/* Regular Input */}
      {!isCheckbox && !isToggle && (
        <div className="relative">
          {LeftIcon && (
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <LeftIcon className="w-4 h-4" />
            </span>
          )}
          <input
            className={`
              w-full py-2.5 text-sm
              border rounded-xl
              focus:ring-2 focus:ring-primary/40
              focus:border-primary
              transition-all duration-200
              placeholder:text-gray-400 text-gray-800
              ${LeftIcon ? "pl-10" : "px-4"}
              ${isPassword ? "pr-11" : "pr-4"}
              ${disable ? "bg-gray-100 cursor-not-allowed" : "bg-white hover:border-gray-400"}
              ${meta.touched && meta.error ? "border-red-500 focus:ring-red-500/30 focus:border-red-500" : "border-gray-200"}
              ${props.className || ""}
            `}
            {...field}
            {...props}
            autoComplete="off"
            disabled={disable}
            type={isPassword && !showPassword ? "password" : props.type || "text"}
            aria-invalid={meta.touched && meta.error ? "true" : "false"}
            aria-describedby={meta.touched && meta.error ? `${props.name}-error` : undefined}
          />

          {/* Password Toggle */}
          {isPassword && (
            <button
              type="button"
              className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-primary transition-colors duration-200"
              onClick={onPasswordToggle}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
            </button>
          )}
        </div>
      )}

      {/* Error */}
      {meta.touched && meta.error && (
        <p
          id={`${props.name}-error`}
          className="mt-1 text-xs text-red-600 flex items-center gap-1 font-medium"
          role="alert"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {meta.error}
        </p>
      )}
    </div>
  );
};

export default InputField;

